import React, { useCallback, useMemo, useState } from 'react';
import { Tooltip } from '@ui/atoms/Tooltip';
import { Modal, ModalHeader, ModalBody } from '@ui/molecules/Modal';
import { translate } from '@utils/i18n';
import './PaymentLifecycleIconsStyles.scss';
// Mapping per Mason's spec — mirrors the Payrix portal palette:
//   - Blue check  : approved (auth-only)
//   - Green check : captured + settled (funds in motion / disbursed)
//   - Grey x      : failed (decline)
//   - Grey minus  : returned (ACH return)
//   - Left-share  : refunded (rendered against the ORIGINAL row); a small
//     asterisk badge overlays this icon when the refund is partial.
const STATUS_ICONS = {
    approved: { iconClass: 'fa-solid fa-circle-check', modifier: 'approved' },
    captured: { iconClass: 'fa-solid fa-circle-check', modifier: 'captured' },
    settled: { iconClass: 'fa-solid fa-circle-check', modifier: 'settled' },
    failed: { iconClass: 'fa-solid fa-circle-xmark', modifier: 'failed' },
    returned: { iconClass: 'fa-solid fa-circle-minus', modifier: 'returned' },
    refunded: { iconClass: 'fa-solid fa-share fa-flip-horizontal', modifier: 'refunded' },
    voided: { iconClass: 'fa-solid fa-ban', modifier: 'voided' },
    // Gavel reads as "dispute under review" — neutral pending state.
    chargeback_opened: { iconClass: 'fa-solid fa-gavel', modifier: 'chargeback-opened' },
    // Won → green check shield: outcome favorable to the merchant.
    chargeback_won: { iconClass: 'fa-solid fa-shield-check', modifier: 'chargeback-won' },
    // Lost → red gavel: outcome unfavorable, payment row is greyed in this case.
    chargeback_lost: { iconClass: 'fa-solid fa-gavel', modifier: 'chargeback-lost' },
    // Manual correction → wrench: a support/admin action (not a gateway event) —
    // e.g. a returned payment voided by hand before the webhook auto-handles it.
    manual_correction: { iconClass: 'fa-solid fa-wrench', modifier: 'manual-correction' }
};
const I18N_NAMESPACE = 'invoices.show.payment_lifecycle_icons';
function formatTimestamp(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return iso;
    return d.toLocaleString();
}
function eventLabel(event) {
    if (event.status === 'refunded' && event.partial) {
        return translate('statuses.refunded_partial', { namespace: I18N_NAMESPACE });
    }
    return translate(`statuses.${event.status}`, { namespace: I18N_NAMESPACE });
}
function tooltipContent(event) {
    // Intentionally NO `details` field — return descriptions and failure
    // reasons can be long. The modal renders them; tooltip stays compact.
    return (React.createElement("div", { className: "payment-lifecycle-icons__tooltip" },
        React.createElement("strong", null, eventLabel(event)),
        React.createElement("span", null, formatTimestamp(event.occurredAt))));
}
function HistoryRow({ event }) {
    const descriptor = STATUS_ICONS[event.status];
    return (React.createElement("li", { className: "payment-lifecycle-icons__history-row" },
        React.createElement("span", { className: `payment-lifecycle-icons__slot payment-lifecycle-icons__slot--${descriptor.modifier} payment-lifecycle-icons__history-icon`, "aria-hidden": "true" },
            React.createElement("i", { className: `${descriptor.iconClass} payment-lifecycle-icons__icon` })),
        React.createElement("span", { className: "payment-lifecycle-icons__history-text" },
            React.createElement("strong", null, eventLabel(event)),
            React.createElement("span", { className: "payment-lifecycle-icons__history-timestamp" }, formatTimestamp(event.occurredAt)),
            event.details ? React.createElement("span", { className: "payment-lifecycle-icons__history-detail" }, event.details) : null)));
}
export function PaymentLifecycleIconsView({ event, history }) {
    const [isModalOpen, setModalOpen] = useState(false);
    const openModal = useCallback(() => setModalOpen(true), []);
    const closeModal = useCallback(() => setModalOpen(false), []);
    // Show in newest-first order in the modal so the most relevant context is
    // at the top of the dialog — matches conversational scrollback patterns.
    const chronologicalHistory = useMemo(() => {
        if (!history || history.length === 0)
            return [];
        return [...history].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
    }, [history]);
    if (!event)
        return null;
    const descriptor = STATUS_ICONS[event.status];
    if (!descriptor)
        return null;
    const hasHistory = chronologicalHistory.length > 1;
    const slotClass = `payment-lifecycle-icons__slot payment-lifecycle-icons__slot--${descriptor.modifier}`;
    const innerIcon = (React.createElement(React.Fragment, null,
        React.createElement("i", { className: `${descriptor.iconClass} payment-lifecycle-icons__icon`, "aria-hidden": "true" }),
        event.status === 'refunded' && event.partial ? (React.createElement("span", { className: "payment-lifecycle-icons__asterisk", "aria-hidden": "true" }, "*")) : null));
    const iconElement = hasHistory ? (React.createElement("button", { type: "button", className: `${slotClass} payment-lifecycle-icons__slot--clickable`, "aria-label": eventLabel(event), onClick: openModal }, innerIcon)) : (React.createElement("span", { className: slotClass, role: "img", "aria-label": eventLabel(event) }, innerIcon));
    return (React.createElement(React.Fragment, null,
        React.createElement(Tooltip, { content: tooltipContent(event), position: "top" }, iconElement),
        hasHistory ? (React.createElement(Modal, { isOpen: isModalOpen, onClose: closeModal },
            React.createElement(ModalHeader, { title: translate('history_title', { namespace: I18N_NAMESPACE }), onClose: closeModal }),
            React.createElement(ModalBody, null,
                React.createElement("ol", { className: "payment-lifecycle-icons__history-list" }, chronologicalHistory.map((e, idx) => (React.createElement(HistoryRow, { key: `${e.status}-${e.occurredAt}-${idx}`, event: e }))))))) : null));
}
